import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User } from './TableData';

interface UserActionsMenuProps {
  user: User;
  onDelete: (user: User) => void;
  onWhitelist: (userId: string) => void;
}

const UserActionsMenu: React.FC<UserActionsMenuProps> = ({
  user,
  onDelete,
  onWhitelist,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 180;
      const menuHeight = 100; // approximate height

      // Calculate position
      let left = rect.right - menuWidth;
      let top = rect.bottom + 4;

      // Adjust if menu goes off screen
      if (left < 10) {
        left = 10;
      }
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 4;
      }

      setMenuPosition({ top, left });
    }
  }, [isOpen]);

  const handleToggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleWhitelist = (e: React.MouseEvent) => {
    e.stopPropagation();
    onWhitelist(user._id);
    setIsOpen(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(user);
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggleMenu}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Actions"
      >
        <img
          src="/assets/actions.svg"
          alt="Actions"
          className="w-[20px] h-[20px]"
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed w-[180px] bg-white border border-[#E5E9EB] rounded-xl shadow-xl py-1"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              zIndex: 9999,
            }}
          >
            <button
              onClick={handleWhitelist}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
            >
              <img
                src={
                  user.isWhitelisted
                    ? '/assets/close.svg'
                    : '/assets/whiteCheck.svg'
                }
                alt=""
                className="w-[16px] h-[16px]"
              />
              {user.isWhitelisted
                ? 'Remove from Whitelist'
                : 'Add to Whitelist'}
            </button>

            <div className="border-t border-[#E5E9EB] my-1" />

            <button
              onClick={handleDelete}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
            >
              <img
                src="/assets/RedCross.svg"
                alt=""
                className="w-[16px] h-[16px]"
              />
              Delete User
            </button>
          </div>,
          document.body,
        )}
    </>
  );
};

export default UserActionsMenu;
